import { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import './Historial.css';
import Loader from '../../components/loading/Loader';
import {
    Box,
    Button,
    Chip,
    List,
    ListItemButton,
    Pagination,
    Paper,
    Stack,
    Typography
} from '@mui/material';

import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const TURNOS_POR_PAGINA = 8;
const RESUMEN_INICIAL = {
    total: 0,
    reservados: 0,
    realizados: 0,
    cancelados: 0
};
const FILTROS_ESTADO = [
    { valor: 'TODOS', label: 'Todos' },
    { valor: 'RESERVADO', label: 'Reservados' },
    { valor: 'REALIZADO', label: 'Realizados' },
    { valor: 'CANCELADO', label: 'Cancelados' }
];
const ESTADO_STYLES = {
    reservado: { backgroundColor: '#e3f2fd', color: '#1976d2' },
    realizado: { backgroundColor: '#dcfce7', color: '#166534' },
    cancelado: { backgroundColor: '#f3f4f6', color: '#6b7280' }
};

function obtenerNombreServicio(turno) {
    return turno.especialidad?.nombre ?? turno.practica?.nombre ?? '-';
}

function obtenerFecha(fechaHora) {
    return new Date(fechaHora).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function obtenerHora(fechaHora) {
    return new Date(fechaHora).toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit'
    });
}

function obtenerClaseEstado(estado = '') {
    return estado.toLowerCase();
}

function ordenarCambiosPorFecha(a, b) {
    return new Date(a.fechaHoraIngreso) - new Date(b.fechaHoraIngreso);
}

function EstadoChip({ estado }) {
    const claseEstado = obtenerClaseEstado(estado);

    return (
        <Chip
            label={estado}
            size="small"
            sx={{
                ...ESTADO_STYLES[claseEstado],
                minWidth: 96,
                borderRadius: '999px',
                fontSize: '0.78rem',
                fontWeight: 700
            }}
        />
    );
}

function ResumenItem({ label, value }) {
    return (
        <Paper className="historial_resumen_item" elevation={0}>
            <Typography component="span">{label}</Typography>
            <Typography component="strong">{value}</Typography>
        </Paper>
    );
}

function DatoDetalle({ icono, children }) {
    return (
        <Stack className="historial_dato" direction="row" alignItems="center" gap={0.75}>
            {icono}
            <Typography component="span">{children}</Typography>
        </Stack>
    );
}

function Historial() {
    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [turnoSeleccionadoId, setTurnoSeleccionadoId] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('TODOS');
    const [paginaActual, setPaginaActual] = useState(1);
    const [paginacion, setPaginacion] = useState(null);
    const [resumen, setResumen] = useState(RESUMEN_INICIAL);

    const cargarHistorial = useCallback(async() =>{
        try {
            const params = {
                page: paginaActual,
                limit: TURNOS_POR_PAGINA
            };

            if (filtroEstado !== 'TODOS') {
                params.estado = filtroEstado;
            }

            const res = await axiosInstance.get('/turnos/historial', { params });

            setTurnos(res.data.data ?? []);
            setPaginacion(res.data.paginacion ?? null);
            setResumen(res.data.resumen ?? RESUMEN_INICIAL);
        }
        catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    },[filtroEstado, paginaActual])

     useEffect(() => {
        cargarHistorial();
    }, [cargarHistorial]);

    const totalPaginas = Math.max(1, paginacion?.totalPaginas ?? 1);
    const totalFiltrado = paginacion?.total ?? turnos.length;

    useEffect(() => {
        if (paginaActual > totalPaginas) {
            setPaginaActual(totalPaginas);
        }
    }, [paginaActual, totalPaginas]);

    useEffect(() => {
        if (turnos.length === 0) {
            setTurnoSeleccionadoId(null);
            return;
        }

        const existeSeleccion = turnos.some(turno => turno._id === turnoSeleccionadoId);

        if (!existeSeleccion) {
            setTurnoSeleccionadoId(turnos[0]._id);
        }
    }, [turnos, turnoSeleccionadoId]);

    const turnoSeleccionado = useMemo(
        () => turnos.find(turno => turno._id === turnoSeleccionadoId) ?? null,
        [turnos, turnoSeleccionadoId]
    );

    const historialEstadoOrdenado = useMemo(
        () => [...(turnoSeleccionado?.historialEstado ?? [])].sort(ordenarCambiosPorFecha),
        [turnoSeleccionado]
    );

    if (loading) return <Loader mensaje="Cargando historial..." />;

    return (
        <Box className="turnos">

            <Box component="section" className="turnos_section">

                <Box component="header" className="turnos_header">
                    Historial de Turnos
                </Box>

                {resumen.total === 0 ? (
                    <Box className="historial_vacio">
                        <Typography>No hay turnos registrados.</Typography>
                    </Box>
                ) : (
                    <>
                        <Box className="historial_resumen">
                            <ResumenItem label="Total de turnos" value={resumen.total} />
                            <ResumenItem label="Reservados" value={resumen.reservados} />
                            <ResumenItem label="Realizados" value={resumen.realizados} />
                            <ResumenItem label="Cancelados" value={resumen.cancelados} />
                        </Box>

                        <Stack
                            className="historial_filtros"
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            aria-label="Filtrar turnos por estado"
                        >
                            {FILTROS_ESTADO.map(filtro => (
                                <Button
                                    key={filtro.valor}
                                    type="button"
                                    variant={filtroEstado === filtro.valor ? 'contained' : 'outlined'}
                                    className="historial_filtro"
                                    onClick={() => {
                                        setFiltroEstado(filtro.valor);
                                        setPaginaActual(1);
                                    }}
                                >
                                    {filtro.label}
                                </Button>
                            ))}
                        </Stack>

                        <Box className="historial_layout">

                            <Box>
                                <Paper className="historial_lista" elevation={0} aria-label="Turnos del historial">

                                    {turnos.length === 0 ? (
                                        <Typography className="historial_sin_resultados">
                                            No hay turnos para este estado.
                                        </Typography>
                                    ) : (
                                        <List disablePadding>
                                            {turnos.map(turno=>(

                                                <ListItemButton
                                                    key={turno._id}
                                                    className={`historial_fila ${turnoSeleccionadoId === turno._id ? 'historial_fila--activa' : ''}`}
                                                    selected={turnoSeleccionadoId === turno._id}
                                                    onClick={() => setTurnoSeleccionadoId(turno._id)}
                                                >
                                                    <Stack className="historial_fila_fecha" direction="row" alignItems="center" gap={0.75}>
                                                        <EventIcon className="historial_icono"/>
                                                        <Typography component="span">{obtenerFecha(turno.fechaHora)}</Typography>
                                                    </Stack>
                                                    <Typography className="historial_fila_servicio" component="span">
                                                        {obtenerNombreServicio(turno)}
                                                    </Typography>
                                                    <Typography className="historial_fila_medico" component="span">
                                                        {turno.medico?.nombre ?? 'Sin médico asignado'}
                                                    </Typography>
                                                    <EstadoChip estado={turno.estado} />
                                                </ListItemButton>

                                            ))}
                                        </List>
                                    )}

                                </Paper>

                                {totalFiltrado > TURNOS_POR_PAGINA && (
                                    <Stack className="historial_paginacion" direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
                                        <Typography component="span">
                                            Mostrando {(paginaActual - 1) * TURNOS_POR_PAGINA + 1}
                                            -
                                            {Math.min(paginaActual * TURNOS_POR_PAGINA, totalFiltrado)} de {totalFiltrado}
                                        </Typography>

                                        <Pagination
                                            count={totalPaginas}
                                            page={paginaActual}
                                            onChange={(_, pagina) => setPaginaActual(pagina)}
                                            color="primary"
                                            size="small"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Stack>
                                )}
                            </Box>

                            {turnoSeleccionado && (
                                <Paper component="aside" className="historial_detalle" elevation={0}>
                                    <Stack className="historial_detalle_header" direction="row" justifyContent="space-between" gap={2}>
                                        <Box>
                                            <Typography className="historial_detalle_label" component="span">
                                                Turno seleccionado
                                            </Typography>
                                            <Typography component="h3">
                                                {obtenerNombreServicio(turnoSeleccionado)}
                                            </Typography>
                                        </Box>
                                        <EstadoChip estado={turnoSeleccionado.estado} />
                                    </Stack>

                                    <Stack className="historial_detalle_datos">
                                        <DatoDetalle icono={<EventIcon className="historial_icono"/>}>
                                            {obtenerFecha(turnoSeleccionado.fechaHora)} - {obtenerHora(turnoSeleccionado.fechaHora)}
                                        </DatoDetalle>

                                        <DatoDetalle icono={<PersonIcon className="historial_icono"/>}>
                                            Médico: {turnoSeleccionado.medico?.nombre ?? '-'}
                                        </DatoDetalle>

                                        <DatoDetalle icono={<LocationOnIcon className="historial_icono" />}>
                                            Sede: {turnoSeleccionado.sede?.nombre ?? '-'}
                                        </DatoDetalle>

                                        <DatoDetalle icono={<LocalHospitalIcon className="historial_icono"/>}>
                                            {obtenerNombreServicio(turnoSeleccionado)}
                                        </DatoDetalle>

                                        {turnoSeleccionado.costo !== undefined && (
                                            <Typography className="historial_costo">
                                                Costo: $ {turnoSeleccionado.costo}
                                            </Typography>
                                        )}
                                    </Stack>

                                    <Box className="historial_estados">
                                        <Typography component="h4">Historial de estados</Typography>
                                        {historialEstadoOrdenado.length === 0 ? (
                                            <Typography className="historial_sin_estados">
                                                Sin cambios registrados.
                                            </Typography>
                                        ) : (
                                            <ol>
                                                {historialEstadoOrdenado.map((cambio,index)=>(

                                                    <li key={index} className="historial_estado_item">
                                                        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="baseline">
                                                            <Typography component="strong">{cambio.estado}</Typography>
                                                            <Typography component="span">
                                                                {obtenerFecha(cambio.fechaHoraIngreso)} - {obtenerHora(cambio.fechaHoraIngreso)}
                                                            </Typography>
                                                        </Stack>
                                                        {cambio.motivo &&(
                                                            <Typography>{cambio.motivo}</Typography>
                                                        )}
                                                    </li>
                                                ))}
                                            </ol>
                                        )}
                                    </Box>
                                </Paper>
                            )}

                        </Box>
                    </>
                )}

            </Box>

        </Box>
    );

}

export default Historial;
